/**
 * UCM报文管理模块常量定义
 */

import {
    ULD_CORE_STATUS_NAMES,
    ULD_AVAILABILITY_NAMES,
} from '../../UldManagement/constants/uldConstantsV3';

// UCM报文类型
export const UCM_TYPES = {
    OUT: { value: 'OUT', label: 'UCM OUT (出港)', color: 'blue' },
    IN: { value: 'IN', label: 'UCM IN (进港)', color: 'green' },
};

// UCM报文类型选项（用于下拉框）
export const UCM_TYPE_OPTIONS = Object.values(UCM_TYPES).map(type => ({
    value: type.value,
    label: type.label,
}));

// UCM历史记录表格列配置
export const UCM_HISTORY_COLUMNS = [
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 80,
    },
    {
        title: '航班号',
        dataIndex: 'flight_number',
        key: 'flight_number',
        width: 120,
    },
    {
        title: '航班日期',
        dataIndex: 'flight_date',
        key: 'flight_date',
        width: 150,
    },
    {
        title: '起飞机场',
        dataIndex: 'departure_airport',
        key: 'departure_airport',
        width: 100,
    },
    {
        title: '飞机注册号',
        dataIndex: 'aircraft_reg',
        key: 'aircraft_reg',
        width: 130,
    },
    {
        title: '报文类型',
        dataIndex: 'ucm_type',
        key: 'ucm_type',
        width: 100,
    },
    {
        title: 'ULD数量',
        dataIndex: 'uld_count',
        key: 'uld_count',
        width: 100,
    },
    {
        title: '创建时间',
        dataIndex: 'created_at',
        key: 'created_at',
        width: 180,
    },
    {
        title: '创建人',
        dataIndex: 'created_by_user_id',
        key: 'created_by_user_id',
        width: 120,
    },
    {
        title: '操作',
        key: 'action',
        fixed: 'right',
        width: 180,
    },
];

// 默认分页配置
export const DEFAULT_PAGINATION = {
    current: 1,
    pageSize: 20,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total) => `共 ${total} 条记录`,
    pageSizeOptions: ['10', '20', '50', '100'],
};

// 排序选项
export const SORT_OPTIONS = [
    { value: 'created_at', label: '按创建时间' },
    { value: 'flight_date', label: '按航班日期' },
    { value: 'flight_number', label: '按航班号' },
];

// 排序方向选项
export const SORT_ORDER_OPTIONS = [
    { value: 'desc', label: '降序' },
    { value: 'asc', label: '升序' },
];

/**
 * UCM 入向解析确认相关常量
 */

// 报文来源
export const UCM_INBOUND_SOURCE = {
    email: { value: 'email', label: '邮件自动', color: 'geekblue' },
    manual: { value: 'manual', label: '人工粘贴', color: 'default' },
};

// 报文方向（出港 / 进港）
export const UCM_INBOUND_DIRECTION = {
    OUT: { value: 'OUT', label: '出港 OUT', color: 'blue' },
    IN: { value: 'IN', label: '进港 IN', color: 'green' },
};

export const UCM_INBOUND_DIRECTION_OPTIONS = Object.values(UCM_INBOUND_DIRECTION).map(d => ({
    value: d.value,
    label: d.label,
}));

// 处理状态
export const UCM_INBOUND_STATUS = {
    pending: { value: 'pending', label: '待确认', color: 'orange' },
    confirmed: { value: 'confirmed', label: '已确认', color: 'green' },
    ignored: { value: 'ignored', label: '已忽略', color: 'default' },
    parse_failed: { value: 'parse_failed', label: '解析失败', color: 'red' },
};

// 组的整体状态（list_pending_grouped 返回的 status_summary）
// 与单段 UCM_INBOUND_STATUS 共享 pending/confirmed/ignored/parse_failed 标签；
// 多出 partial：组里 confirmed 与 ignored 混合（无 pending 段）的情况，便于运营一眼看出"组里两段一确一弃"
export const UCM_INBOUND_GROUP_STATUS = {
    ...UCM_INBOUND_STATUS,
    partial: { value: 'partial', label: '部分处理', color: 'gold' },
};

export const UCM_INBOUND_STATUS_OPTIONS = Object.values(UCM_INBOUND_STATUS).map(s => ({
    value: s.value,
    label: s.label,
}));

// 待确认列表的状态页签（替代旧的状态下拉筛选）：
// - 待处理：pending + parse_failed 合并（都是还没了结、要人处理的）；不设时间窗（量本来就少）。
// - 已确认 / 已忽略：历史记录只增不减；默认只看最近 UCM_INBOUND_HISTORY_RECENT_DAYS 天，
//   避免列表随存量增长把全部历史一次性捞回内存而变慢（后端按 created_at 加时间窗，命中复合索引）。
export const UCM_INBOUND_HISTORY_RECENT_DAYS = 90;
export const UCM_INBOUND_TABS = [
    { key: 'active', label: '待处理', status: 'pending,parse_failed' },
    { key: 'confirmed', label: '已确认', status: 'confirmed', recentDays: UCM_INBOUND_HISTORY_RECENT_DAYS },
    { key: 'ignored', label: '已忽略', status: 'ignored', recentDays: UCM_INBOUND_HISTORY_RECENT_DAYS },
];

// 航班匹配状态
export const UCM_INBOUND_MATCH_STATUS = {
    matched: { value: 'matched', label: '已匹配航班', color: 'green' },
    no_flight: { value: 'no_flight', label: '未匹配航班', color: 'red' },
    ambiguous: { value: 'ambiguous', label: '多条候选', color: 'orange' },
};

// 运输方式（卡车转运解析新增；存量记录视作 AIR）
// 颜色取与各状态语义靠近的视觉档：
// - AIR：与"已匹配航班"同语调（蓝），表示走航班流程
// - SURFACE：与位置展示「在途·卡车」一致（橙），不与机场/航班颜色冲突
// - UNDECIDED：与多条候选 / 待复核同档（紫），强调需要人工先判一下
export const UCM_INBOUND_TRANSPORT_MODE = {
    AIR: { value: 'AIR', label: '航班', color: 'blue' },
    SURFACE: { value: 'SURFACE', label: '卡车转运', color: 'orange' },
    UNDECIDED: { value: 'UNDECIDED', label: '待人工判定', color: 'purple' },
};

export const UCM_INBOUND_TRANSPORT_MODE_OPTIONS = Object.values(UCM_INBOUND_TRANSPORT_MODE).map(
    m => ({ value: m.value, label: m.label }),
);

// 容错：存量记录或老接口缺 transport_mode 时按 AIR 处理
export const getTransportMode = (record) => {
    const key = (record?.transport_mode || 'AIR').toUpperCase();
    return UCM_INBOUND_TRANSPORT_MODE[key] || UCM_INBOUND_TRANSPORT_MODE.AIR;
};

// 逐块归宿（每块 ULD 的处理状态；缺失按 pending 处理）
export const UCM_BLOCK_STATUS = {
    pending: { value: 'pending', label: '待处理', color: 'orange' },
    written: { value: 'written', label: '已写台账', color: 'green' },
    ignored: { value: 'ignored', label: '已忽略', color: 'default' },
};

// 强制通过类别（与后端 _force_category / classify_pending / block_force.category 对齐）
// 每块"需强制"只归一个主类别：外放 > 退役 > 位置（与后端口径一致）。
export const UCM_FORCE_CATEGORY = {
    location: { value: 'location', label: '位置不符', color: 'gold', hint: '当前位置与报文目标不符，强制将跳过一致性校验直接写' },
    delivered: { value: 'delivered', label: '已外放', color: 'volcano', hint: '已外放给收货人，强制将先自动撤销外放再写' },
    retired: { value: 'retired', label: '退役/停用', color: 'magenta', hint: '已退役/停用，强制将先恢复为在库再写' },
};

export const getForceCategory = (category) =>
    UCM_FORCE_CATEGORY[category] || { value: category, label: category || '需强制', color: 'gold', hint: '需强制通过' };

// 取单块归宿（容错：缺失 / 未知值都按 pending）
export const getBlockStatus = (item) => {
    const key = item?.block_status || 'pending';
    return UCM_BLOCK_STATUS[key] || UCM_BLOCK_STATUS.pending;
};

// 该块是否已定归宿（已写台账 / 已忽略 → 只读）
export const isBlockHandled = (item) =>
    item?.block_status === 'written' || item?.block_status === 'ignored';

export const UCM_REOPEN_TEXT = {
    recordAction: '重新处理',
    recordConfirm: '将保留原记录和已处理块，并按当前航班与 ULD 台账刷新其余预览。',
    recordUnavailable: '仅已忽略的记录可重新处理',
    itemAction: '撤销忽略',
    itemConfirm: '只恢复这一块，并按当前 ULD 台账刷新位置预览。',
    itemRequiresRecord: '请先恢复整条记录，再撤销单块忽略',
    itemUnavailable: '仅已忽略且未写台账的块可撤销忽略',
};

export const canReopenInbound = (record) => record?.status === 'ignored';

export const canReopenInboundItem = (record, item) =>
    !!record && record.status !== 'ignored' && item?.block_status === 'ignored';

export const reopenInboundItemTip = (record, item) => {
    if (record?.status === 'ignored') return UCM_REOPEN_TEXT.itemRequiresRecord;
    if (item?.block_status === 'written') return '已写台账的块不能撤销';
    if (item?.block_status !== 'ignored') return UCM_REOPEN_TEXT.itemUnavailable;
    return UCM_REOPEN_TEXT.itemConfirm;
};

// 仅当 status=pending 且：
// - AIR：match_status=matched（必须挂上航班）
// - SURFACE：有 surface_trip_number（必须有趟次号）
// - UNDECIDED：必须先改判，不能直接确认
// 时允许确认写台账。
export const canConfirmInbound = (record) => {
    if (!record || record.status !== 'pending') return false;
    const mode = (record.transport_mode || 'AIR').toUpperCase();
    if (mode === 'AIR') return record.match_status === 'matched';
    if (mode === 'SURFACE') return !!(record.surface_trip_number && String(record.surface_trip_number).trim());
    return false; // UNDECIDED 必须先改判
};

// 单个 member（来自 group.members[]）→ 方向列标签的文案与颜色
// 文案规则：`<方向> <板数提示>`；板数为 0 时显示"空"，>0 时显示具体数字，便于运营一眼看出空 IN 段
// 颜色规则：按"段自身状态"上色（避免组级状态盖过单段进度，规格 FR-003）
//   - pending：用方向自身的色（OUT 蓝 / IN 绿）
//   - confirmed：统一绿色（已写台账）
//   - ignored：灰色（已弃）
//   - parse_failed：红色（解析失败的整封报文，通常不进组）
export const memberDirectionTag = (member) => {
    if (!member) return { label: '-', color: 'default' };
    const dir = (member.direction || '').toUpperCase();
    const total = Number(member.uld_total ?? 0);
    const dirLabel = UCM_INBOUND_DIRECTION[dir]?.label?.split(' ')[0] || dir || '-';  // 仅取「出港」/「进港」短文案
    const countLabel = total > 0 ? `${total}` : '空';
    const label = `${dirLabel} ${countLabel}`;
    let color = UCM_INBOUND_DIRECTION[dir]?.color || 'default';
    if (member.status === 'confirmed') color = 'green';
    else if (member.status === 'ignored') color = 'default';
    else if (member.status === 'parse_failed') color = 'red';
    return { label, color };
};

// 一封报文里只剩一个有效段（其它段是空 IN 占位）时，详情切换器不显示
// 但抽屉里仍按 primary 段的内容渲染。这里给出"是否需要在抽屉顶部显示切换器"的判断。
export const shouldShowSegmentSwitcher = (members) => Array.isArray(members) && members.length >= 2;

// 该段是不是"空段"——板数为 0，详情里隐藏底部按钮（写台账 / 改板号等都没意义）
export const isEmptyMember = (member) => Number(member?.uld_total ?? 0) === 0;

// 位置对象 → 展示文案（{type:AIRPORT,airport} / {type:FLIGHT,flight_number} / 其它）
// 特殊情况：
// - OTHER_LOCATION + transport：「<other>（趟次 <transport>）」用于卡车转运在途
// - UNDECIDED：占位，让前端能识别"先去改判"，不会被当成普通机场/航班渲染
export const formatInboundLocation = (loc) => {
    if (!loc || typeof loc !== 'object') return '-';
    if (loc.type === 'AIRPORT') return loc.airport ? `机场 ${loc.airport}` : '机场(待定)';
    if (loc.type === 'FLIGHT') return loc.flight_number ? `航班 ${loc.flight_number}` : '航班(待定)';
    if (loc.type === 'UNDECIDED') return '待人工判定';
    if (loc.type === 'OTHER_LOCATION') {
        const base = loc.other || '其它位置';
        return loc.transport ? `${base}（趟次 ${loc.transport}）` : base;
    }
    if (loc.other) return loc.other;
    if (loc.transport) return loc.transport;
    return loc.type || '-';
};

// 状态对象 → 展示文案（{core_status, availability} → "在库 / 可用"）
export const formatInboundStatus = (status) => {
    if (!status || typeof status !== 'object') return '-';
    const core = ULD_CORE_STATUS_NAMES[status.core_status] || status.core_status || '-';
    const avail = ULD_AVAILABILITY_NAMES[status.availability] || status.availability;
    return avail ? `${core} / ${avail}` : core;
};
